import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IconRegistry } from '../../services/icon-registry';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

/**
 * The app's only icon mechanism: an SVG from IconRegistry, injected inline so
 * that `fill="currentColor"` picks up the colour of whatever renders it.
 *
 * It used to have a second mode that printed a Material Icons ligature into
 * an `<i class="material-icons">`. Six glyphs in three components were paying
 * for a 128 KB font and a render-blocking stylesheet on every page, so they
 * moved to registered SVGs and the mode went with them.
 */
@Component({
  selector: 'app-icon',
  template: ` <ng-content></ng-content> `,
  styles: [
    `
      :host {
        display: inline-block;
        width: 24px;
        height: 24px;
      }

      :host(.icon-transitioning) {
        opacity: 0.1;
        transform: scale(0.5);
      }
    `,
  ],
})
export class IconComponent implements OnInit, OnChanges, OnDestroy {
  @Input() svgIcon: string = '';

  private subscription: Subscription | undefined;

  constructor(
    private elementRef: ElementRef,
    private iconRegistry: IconRegistry,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.setIcon();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['svgIcon']) {
      this.animateChange();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private setIcon(): void {
    if (!this.svgIcon) return;

    this.subscription = this.iconRegistry
      .getNamedSvgIcon(this.svgIcon)
      .subscribe({
        next: (url) => {
          // Get the SVG string from the SafeResourceUrl
          const urlStr = this.sanitizer.sanitize(4, url);

          if (urlStr) {
            this.iconRegistry
              .fetchSvgContent(urlStr)
              .then((svgContent) => {
                // Set the SVG content to the element
                this.elementRef.nativeElement.innerHTML = svgContent;

                // Make sure the SVG fills the container
                const svg = this.elementRef.nativeElement.querySelector('svg');
                if (svg) {
                  svg.setAttribute('width', '100%');
                  svg.setAttribute('height', '100%');
                  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                }
              })
              .catch((error) =>
                console.error('Error loading SVG icon:', error)
              );
          } else {
            console.error('Invalid URL for SVG icon:', this.svgIcon);
          }
        },
        // getNamedSvgIcon throws for a name nobody registered. That is a
        // missing entry in IconRegistryService, not a runtime failure of the
        // page: report it and leave the slot empty rather than letting an
        // unhandled rejection tear the view down around one icon.
        error: (e: Error) =>
          console.error(`[app-icon] ${e.message}`, this.svgIcon),
      });
  }

  private animateChange(): void {
    const el = this.elementRef.nativeElement as HTMLElement;

    el.classList.add('icon-transitioning');

    // Wait for the transition to finish
    setTimeout(() => {
      this.setIcon(); // replace the actual SVG content
      el.classList.remove('icon-transitioning');
    }, 60);
  }
}
